import React from 'react';
import { AxiosError, AxiosResponse } from 'axios';
import { Grid, TextField, FormControl, FormLabel, FormControlLabel, Checkbox } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';

import { ClientContext } from '../ClientContext';
import ClientService from '../../../Services/ClientService';
import { clientModelInitial } from '../../../Models/ClientModel';
import { PageLocation } from '../../../shared/enum/PageLocation';
import { HomeContext } from '../../Home/HomeContext';
import ContentClientService from '../../../Services/ContentClientService';
import VehicleClientService from '../../../Services/VehicleClientService';
import KeywordService from '../../../Services/KeywordService';
import SourceService from '../../../Services/SourceService';
import ExtraParameterService from '../../../Services/ExtraParameterService';
import OrganogramService from '../../../Services/OrganogramService';
import { OrganogramModel } from '../../../Models/OrganogramModel';
import { VehicleModel } from '../../../Models/VehicleModel';
import { KeywordModel } from '../../../Models/KeywordModel';
import { SourceModel } from '../../../Models/SourceModel';
import { ExtraParameterModel } from '../../../Models/ExtraParameterModel';
import ClientClipperService from '../../../Services/ClientClipperService';
import { BasicPage } from '../../../shared/components/BasicPage';
import ClientCutoutService from '../../../Services/ClientCutoutService';
import { ClientMenu } from '../components/ClientMenu';
import { ClientSave } from '../components/ClientSave';
import { ClientPages } from '../components/ClientPages';
import { ClientErrorContext } from '../ClientErrorContext';
import { ClientPageTabs } from '../enums/ClientPageTabs';
import { ClientNavigation } from '../components/ClientNavigation';
import ClientIdmConversionRateService from '../../../Services/ClientIdmConversionRateService';
import ClientIrsConversionRateService from '../../../Services/ClientIrsConversionRateService';
import { ClientProductField } from '../ClientProduct/components/ClientProductField';
import ClientProductService from '../../../Services/ClientProductService';

const useStyles = makeStyles(() => createStyles({
    itens: {
        width: '100%',
        color: 'primary'
    },
}));

export function ClientInput() {
    const classes = useStyles();
    
    const clientContext = React.useContext(ClientContext);  
    const homeContext = React.useContext(HomeContext); 

    const [nameNotDefinedError, setNameNotDefinedError] = React.useState<boolean>(false);
    const [productNotSelectedError, setProductNotSelectedError] = React.useState<boolean>(false);
    const [vehicleNotDefinedError, setVehicleNotDefinedError] = React.useState<boolean>(false);
    const [keywordsNotDefinedError, setKeywordsNotDefinedError] = React.useState<boolean>(false);
    const [sourceNotDefinedError, setSourceNotDefinedError] = React.useState<boolean>(false);
    const [extraParameterNotDefinedError, setExtraParameterNotDefinedError] = React.useState<boolean>(false);
    const [contentNotDefinedError, setContentNotDefinedError] = React.useState<boolean>(false);
    const [avatarFileError, setAvatarFileError] = React.useState<boolean>(false);
    const [primaryColorNotDefinedError, setPrimaryColorNotDefinedError] = React.useState<boolean>(false);
    const [secondaryColorNotDefinedError, setSecondaryColorNotDefinedError] = React.useState<boolean>(false);
    const [primaryColorError, setPrimaryColorError] = React.useState<boolean>(false);
    const [secondaryColorError, setSecondaryColorError] = React.useState<boolean>(false);
    const [organogramNotSelectedInCutoutError, setOrganogramNotSelectedInCutoutError] = React.useState<boolean>(false);

    const [clientLoading, setClientLoading] = React.useState<boolean>(false);
    const [contentLoading, setContentLoading] = React.useState<boolean>(false);
    const [vehicleLoading, setVehicleLoading] = React.useState<boolean>(false);
    const [keywordLoading, setKeywordLoading] = React.useState<boolean>(false);
    const [sourceLoading, setSourceLoading] = React.useState<boolean>(false);
    const [extraParameterLoading, setExtraParameterLoading] = React.useState<boolean>(false);
    const [organogramLoading, setOrganogramLoading] = React.useState<boolean>(false);
    const [clipperLoading, setClipperLoading] = React.useState<boolean>(false);
    const [cutoutLoading, setCutoutLoading] = React.useState<boolean>(false);
    const [clientIdmRateLoading, setClientIdmRateLoading] = React.useState<boolean>(false);
    const [clientIrsRateLoading, setClientIrsRateLoading] = React.useState<boolean>(false);
    const [clientProductsLoading, setClientProductsLoading] = React.useState<boolean>(false);

    React.useEffect(() => {
        if (homeContext.pageLocation === PageLocation.CLIENT_EDIT && clientContext.client.id > 0)
            fetchEditClient();
    }, []);

    React.useEffect(() => {
        const isLoading = 
            clientLoading ||
            contentLoading ||
            vehicleLoading ||
            keywordLoading ||
            sourceLoading ||
            extraParameterLoading ||
            organogramLoading ||
            clipperLoading ||
            cutoutLoading ||
            clientIdmRateLoading ||
            clientIrsRateLoading ||
            clientProductsLoading;

        if (homeContext.openLoadingDialog && !isLoading) {
            homeContext.setOpenLoadingDialog(false);
        }
    }, [clientLoading, contentLoading, vehicleLoading, keywordLoading, sourceLoading, extraParameterLoading,
        organogramLoading, clipperLoading, cutoutLoading, clientIdmRateLoading, clientIrsRateLoading, clientProductsLoading]);

    const fetchEditClient = () => {
        if (clientContext.client) {
            homeContext.setOpenLoadingDialog(true);
            setClientLoading(true);
            setContentLoading(true);
            setVehicleLoading(true);
            setKeywordLoading(true);
            setSourceLoading(true);
            setExtraParameterLoading(true);
            setOrganogramLoading(true);
            setClipperLoading(true);
            setCutoutLoading(true);
            setClientIdmRateLoading(true);
            setClientIrsRateLoading(true);
            setClientProductsLoading(true);

            ClientService.clientService()
                .get(clientContext.client.id)
                .then((res: AxiosResponse) => {
                    if (res.data) {
                        const client = res.data;
                        clientContext.setClient(client);
                        clientContext.setPrimaryColor(client.primaryColor ?? '');
                        clientContext.setSecondaryColor(client.secondaryColor ?? '');
                        clientContext.setStockSymbol(client.stockSymbol ?? '');
                    }
                })
                .catch((error: AxiosError) => homeContext.showError('Ops.. ocorreu um erro ao carregar os dados do cliente. <br/>', error))
                .finally(() => setClientLoading(false));

            ContentClientService.contentClientService(clientContext.client.id)
                .getAll()
                .then((res: AxiosResponse) => {
                    if (res.data)
                        clientContext.setContents(res.data);
                })
                .catch((error: AxiosError) => homeContext.showError('Ops.. ocorreu um erro ao carregar os teores do cliente. <br/>', error))
                .finally(() => setContentLoading(false));

            VehicleClientService.vehicleClientService(clientContext.client.id)
                .getAll()
                .then((res: AxiosResponse) => {
                    if (res.data) {                      
                        const sorted = (res.data as VehicleModel[]).sort((a, b) => 
                            ('' + a.nomenclature).localeCompare('' + b.nomenclature));  
                        clientContext.setVehicles(sorted);
                    }
                })
                .catch((error: AxiosError) => homeContext.showError('Ops.. ocorreu um erro ao carregar os veículos do cliente. <br/>', error))
                .finally(() => setVehicleLoading(false));

            KeywordService.keywordService(clientContext.client.id)
                .getAll()
                .then((res: AxiosResponse) => {
                    setKeywordLoading(false);
                    if (res.data) {
                        const sorted = (res.data as KeywordModel[]).sort((a, b) => 
                            (a.description).localeCompare(b.description));                           
                        clientContext.setKeywords(sorted);
                    }
                })
                .catch((error: AxiosError) => homeContext.showError('Ops.. ocorreu um erro ao carregar as palavras chaves do cliente. <br/>', error))
                .finally(() => setKeywordLoading(false));

            SourceService.sourceService(clientContext.client.id)
                .getAll()
                .then((res: AxiosResponse) => {
                    if (res.data) {
                        const sorted = (res.data as SourceModel[]).sort((a, b) => 
                            (a.name).localeCompare(b.name)); 
                        clientContext.setSources(sorted);
                    }
                })
                .catch((error: AxiosError) => homeContext.showError('Ops.. ocorreu um erro ao carregar as fontes do cliente. <br/>', error))
                .finally(() => setSourceLoading(false));

            ExtraParameterService
                .getExtraParameterByQuery(clientContext.client.id, true)
                .then((res: AxiosResponse) => {
                    if (res.data) {                
                        const sorted = (res.data as ExtraParameterModel[]).sort((a, b) => 
                            (a.description).localeCompare(b.description));                 
                        clientContext.setExtraParameters(sorted);
                    }
                })
                .catch((error: AxiosError) => homeContext.showError('Ops.. ocorreu um erro ao carregar as mensagens chave do cliente. <br/>', error))
                .finally(() => setExtraParameterLoading(false));        
                
            OrganogramService
                .organogramGetAll(clientContext.client.id, false)
                .then((res: AxiosResponse) => {
                    if (res.data) {                                
                        clientContext.setFlatOrganogram(res.data);
                        const organogramId = Math.max(...res.data.map( (o: OrganogramModel) => o.id), 0);
                        clientContext.setOrganogramId(organogramId);
                    }
                })
                .catch((error: AxiosError) => homeContext.showError('Ops.. ocorreu um erro ao carregar o organograma do cliente. <br/>', error))
                .finally(() => setOrganogramLoading(false));

            ClientClipperService.clientClipperService(clientContext.client.id)
                .getAll()
                .then((res: AxiosResponse) => {
                    if (res.data)
                        clientContext.setClientClippers(res.data);
                })
                .catch((error: AxiosError) => homeContext.showError('Ops.. ocorreu um erro ao carregar as clipadoras do cliente. <br/>', error))
                .finally(() => setClipperLoading(false));

            ClientCutoutService.clientCutoutService(clientContext.client.id)
                .getAll()
                .then((res: AxiosResponse) => {
                    if (res.data)
                        clientContext.setCutouts(res.data);
                })
                .catch((error: AxiosError) => homeContext.showError('Ops.. ocorreu um erro ao carregar os recortes do cliente. <br/>', error))
                .finally(() => setCutoutLoading(false));

            ClientIdmConversionRateService.clientIdmConversionRateService(clientContext.client.id)
                .getAll()
                .then((res: AxiosResponse) => {
                    if (res.data)
                        clientContext.setIdmConversionRateList(res.data);
                })
                .catch((error: AxiosError) => homeContext.showError('Ops.. ocorreu um erro ao carregar as taxas de conversão do IDM do cliente. <br/>', error))
                .finally(() => setClientIdmRateLoading(false));

            ClientIrsConversionRateService.clientIrsConversionRateService(clientContext.client.id)
                .getAll()
                .then((res: AxiosResponse) => {
                    if (res.data)
                        clientContext.setIrsConversionRateList(res.data);
                })
                .catch((error: AxiosError) => homeContext.showError('Ops.. ocorreu um erro ao carregar as taxas de conversão do IRS do cliente. <br/>', error))
                .finally(() => setClientIrsRateLoading(false));

            ClientProductService.clientProductService(clientContext.client.id)
                .getAll()
                .then((res: AxiosResponse) => {
                    if (res.data)
                        clientContext.setProducts(res.data);
                })
                .catch((error: AxiosError) => homeContext.showError('Ops.. ocorreu um erro ao carregar os produtos associados ao cliente. <br/>', error))
                .finally(() => setClientProductsLoading(false));
        }
    };


    React.useEffect(() => {
        if (homeContext.pageLocation != PageLocation.CLIENT_EDIT) {
            clientContext.setClient(clientModelInitial);
            clientContext.setVehicles([]);
            clientContext.setKeywords([]);
            clientContext.setSources([]);
            clientContext.setExtraParameters([]);
            clientContext.setContents([]);
            clientContext.setFlatOrganogram([]);
            clientContext.setClientClippers([]);
            clientContext.setCutouts([]);
            clientContext.setCutoutOrganogram([]);
            clientContext.setClientPage(ClientPageTabs.DEFAULT);
            clientContext.setEditMode(false);
            clientContext.setIdmConversionRateList([]);
            clientContext.setIrsConversionRateList([]);
            clientContext.setClientAvatarFile(null);
            clientContext.setPrimaryColor('');
            clientContext.setSecondaryColor('');
            clientContext.setStockSymbol('');
            clientContext.setIdmConversionRateList([]);
            clientContext.setIrsConversionRateList([]);
            clientContext.setProducts([]);
        }
    }, [homeContext.pageLocation]);    


    const getName = () => {
        return (
            <Grid item xs={4} >
                <TextField
                    label="Nome"
                    variant="outlined"
                    fullWidth
                    value={clientContext.client.name}
                    onChange={(event) =>
                        clientContext.setClient({ ...clientContext.client, name: event.target.value })
                    }
                    className={classes.itens}
                    inputProps={{ maxLength: 200 }}   
                    error={nameNotDefinedError && !clientContext.client.name}
                    helperText={(nameNotDefinedError && !clientContext.client.name) && 'Campo obrigatório'} />
            </Grid>
        );
    };

    const getMetricId = () => { 
        return (
            <Grid item xs={2}>
                <TextField
                    label="ID do Metric"
                    variant="outlined"
                    fullWidth
                    type="number"
                    value={clientContext.client.codeMetric ? clientContext.client.codeMetric : ''}
                    onChange={(event) =>
                        clientContext.setClient({ ...clientContext.client, codeMetric: parseInt(event.target.value, 10) })
                    }
                    className={classes.itens} />
            </Grid>
        );
    };

    const getProducts = () => {
        return (
            <Grid item xs={4}>
                <ClientProductField />
            </Grid>
        );
    };

    const getStatus = () => {
        return (
            <Grid item xs={2}>
                <FormControl component="fieldset">
                    <FormLabel component="legend">Status</FormLabel>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={clientContext.client.active}
                                onChange={() => clientContext.setClient({ ...clientContext.client, active: !clientContext.client.active })}
                                name="Ativo"
                                color="primary" />
                        }
                        label="Ativo" />
                </FormControl>
            </Grid>
        );
    };

    return (
        <BasicPage
            title={clientContext.editMode
                ? 'Edição de cliente'
                : 'Cadastro de cliente'}
            subtitle={clientContext.editMode
                ? 'Edite os campos que deseja alterar para o cliente.'
                : 'Preencha os campos abaixo para cadastrar um novo cliente.'}>

            <ClientErrorContext.Provider value={{
                nameNotDefinedError, setNameNotDefinedError,
                productNotSelectedError, setProductNotSelectedError,
                vehicleNotDefinedError, setVehicleNotDefinedError,
                keywordsNotDefinedError, setKeywordsNotDefinedError,
                sourceNotDefinedError, setSourceNotDefinedError,
                extraParameterNotDefinedError, setExtraParameterNotDefinedError,
                contentNotDefinedError, setContentNotDefinedError,
                avatarFileError, setAvatarFileError,
                primaryColorNotDefinedError, setPrimaryColorNotDefinedError,
                secondaryColorNotDefinedError, setSecondaryColorNotDefinedError,
                primaryColorError, setPrimaryColorError,
                secondaryColorError, setSecondaryColorError,
                organogramNotSelectedInCutoutError, setOrganogramNotSelectedInCutoutError
            }}>
                { getName() }
                { getMetricId() }
                { getProducts() }
                { clientContext.editMode && getStatus() }
                <ClientMenu />
                <ClientPages />
                <ClientNavigation />
                <ClientSave />
            </ClientErrorContext.Provider>
        </BasicPage>
    );
}